import React, { useEffect, useState } from 'react'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'
import CertificationModal from '../../components/shared/CertificationModal'

const getDaysLeft = (expiry: string) => {
  if (!expiry) return 999
  return Math.ceil((new Date(expiry).getTime() - Date.now()) / 86400000)
}
const getColor = (days: number) => days < 0 ? { color: '#dc2626', bg: '#fef2f2' } : days <= 30 ? { color: '#f59e0b', bg: '#fffbeb' } : { color: '#16a34a', bg: '#f0fdf4' }

export default function CertificationsPage() {
  const { orgId, isSupervisor } = useAuth()
  const [certs, setCerts] = useState<any[]>([])
  const [filter, setFilter] = useState('All')
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'certifications'), where('orgId', '==', orgId)),
      snap => { setCerts(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )
    return () => unsub()
  }, [orgId])

  const filtered = certs.filter(c => {
    const days = getDaysLeft(c.expiryDate)
    if (filter === 'Expiring') return days >= 0 && days <= 30
    if (filter === 'Expired') return days < 0
    return true
  })

  const formatDate = (iso: string) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  return (
    <AppLayout title="Certifications">
      <div className="mb-6 flex gap-4 items-center">
        <button onClick={() => setShowModal(true)}
          className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">
          + Add Certification
        </button>
        {['All', 'Valid', 'Expiring', 'Expired'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border ${filter === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}>
            {f}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Name</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Holder</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Issue Date</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Expiry</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const days = getDaysLeft(c.expiryDate)
              const { color, bg } = getColor(days)
              return (
                <tr key={c.id} className="border-b border-gray-100">
                  <td className="px-5 py-3 font-medium text-gray-900">{c.name}</td>
                  <td className="px-5 py-3 text-gray-600">{c.userName || c.userId || '-'}</td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(c.issueDate)}</td>
                  <td className="px-5 py-3 text-gray-600">{formatDate(c.expiryDate)}</td>
                  <td className="px-5 py-3">
                    <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: bg, color }}>
                      {days < 0 ? 'Expired' : days === 0 ? 'Today' : `${days}d left`}
                    </span>
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-gray-400">No certifications found</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showModal && <CertificationModal onClose={() => setShowModal(false)} onCreated={() => {}} />}
    </AppLayout>
  )
}